import { calculateMemory } from '@/utils/calc-tools';
import flatMap from 'lodash/flatMap';
import { ContainerItem } from '../../container';
import {
  isVolumeClaimTemplateItem,
  VolumeClaimTemplateItem,
  VolumeItem,
  VolumeMountItem,
  VolumeMountType,
  VolumeType,
} from '../../storage-settings';
import { YamlVolumeClaimTemplate } from '../../types';

export const claimTemplatesToYaml = (
  volumes: VolumeItem[],
  namespace: string,
): YamlVolumeClaimTemplate[] => {
  /** 由于 TypeScript 的类型收窄有一定的局限性，直接在数组的 filter 里调用类型守卫函数时，
   * TS 有时无法自动将整个数组的类型从联合类型（VolumeItem[]）收窄为特定子类型（VolumeClaimTemplateItem[]）。
   * 也就是说，虽然 isVolumeClaimTemplateItem 为单个元素提供了正确的类型谓词，
   * 但 TS 无法自动推断 filter 后得到的数组中的每个元素都符合该子类型，因此需要用类型断言告知编译器。
   */
  const volumeClaimTemplates = volumes.filter((item) =>
    isVolumeClaimTemplateItem(item),
  ) as VolumeClaimTemplateItem[];

  return volumeClaimTemplates.map((v) => {
    return {
      metadata: { name: v.volumeName, namespace },
      spec: {
        storageClassName: v.storageClass,
        accessModes: v.accessModes,
        resources: {
          requests: {
            storage: v.volumeCapacity ? `${v.volumeCapacity}Gi` : undefined,
          },
        },
      },
    };
  });
};

export const yamlToClaimTemplates = (
  yamls: YamlVolumeClaimTemplate[],
  allContainters: ContainerItem[] = [],
): VolumeClaimTemplateItem[] => {
  // 根据 volumeName 从容器中过滤出对应的挂载项，并转换为 VolumeMountItem
  const getVolumeMounts = (volumeName: string): VolumeMountItem[] => {
    return flatMap(allContainters, (container) => {
      return (container.volumeMounts || [])
        .filter((vm) => vm.name === volumeName)
        .map((vm) => ({
          containerName: container.containerSetting.containerName,
          mountType: vm.readOnly ? VolumeMountType.ReadOnly : VolumeMountType.ReadWrite,
          mountPath: vm.mountPath,
        }));
    });
  };

  return yamls.map((y) => ({
    type: VolumeType.VOLUME_CLAIM_TEMPLATE,
    volumeName: y.metadata.name,
    storageClass: y.spec.storageClassName,
    accessModes: y.spec.accessModes,
    volumeCapacity: y.spec.resources?.requests?.storage
      ? parseInt(calculateMemory([y.spec.resources.requests.storage], { forceUnit: 'Gi' }), 10)
      : undefined,
    volumeMounts: getVolumeMounts(y.metadata.name),
  }));
};
